// Common types used across the application

export interface User {
  id: string;
  email: string;
  name: string;
  userName?: string;  // Username from backend
  avatar?: string;
  role?: UserRole;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

export interface Board {
  id: string;
  name: string;
  description?: string;
  color?: string;
  workspaceId: string;
  ownerId: string;
  members: string[];
  // Project ID fields (e.g. "IS-2" for the 2nd board in workspace "Island").
  // Null for legacy standalone boards created before this was introduced.
  abbreviation?: string | null;
  boardNumber?: number | null;
  key?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: string;
  boardId: string;
  sprintId?: string;
  epicId?: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  type?: TaskType;
  assigneeId?: string | null;
  /** Full assignee user object from backend relation */
  assignee?: { id: string | number; fullName?: string; userName?: string } | null;
  reporterId?: string;
  /** The user ID of who originally created this task - never changes */
  createdById?: string;
  /** The full user object of who created the task (populated from backend relation) */
  createdBy?: { id: string; fullName?: string; userName?: string };
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  storyPoints?: number;
  tags?: string[];
  attachments?: Attachment[];
  comments?: Comment[];
  subtasks?: Subtask[];
  dependencies?: string[];
  // Task ID fields (e.g. "AE-12"). Null for tasks on a legacy standalone
  // board (no workspace, so no board abbreviation to derive a key from).
  taskNumber?: number | null;
  key?: string | null;
  createdAt: string;
  updatedAt: string;
}


export interface Subtask {
  id: string;
  title: string;
  completed: boolean;
  assigneeId?: string;
  createdAt: string;
}

export interface Comment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
}

export enum TaskStatus {
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  REVIEW = 'review',
  TESTING = 'testing',
  DONE = 'done',
  BLOCKED = 'blocked',
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum TaskType {
  FEATURE = 'feature',
  BUG = 'bug',
  QUALITY = 'quality',
  INFRASTRUCTURE = 'infrastructure',
  RESEARCH = 'research',
  DOCUMENTATION = 'documentation',
}

export interface Sprint {
  id: string;
  boardId: string;
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  status: SprintStatus;
  velocity?: number;
  totalStoryPoints?: number;
  completedStoryPoints?: number;
  createdAt: string;
  updatedAt: string;
}

export enum SprintStatus {
  PLANNING = 'planning',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface TimeLog {
  id: string;
  taskId: string;
  userId: string;
  userName: string;
  description?: string;
  hours: number;
  logDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface Epic {
  id: string;
  boardId: string;
  name: string;
  description?: string;
  color?: string;
  status: EpicStatus;
  ownerId?: string;
  startDate?: string;
  endDate?: string;
  totalStoryPoints?: number;
  completedStoryPoints?: number;
  progressPercentage?: number;
  totalTasks?: number;
  completedTasks?: number;
  createdAt: string;
  updatedAt: string;
}

export enum EpicStatus {
  PLANNING = 'planning',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ON_HOLD = 'on_hold',
  CANCELLED = 'cancelled',
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  color?: string;
  visibility: WorkspaceVisibility;
  ownerId: string;
  owner?: User;
  members: WorkspaceMember[];
  boards: string[];
  boardCount?: number;
  memberCount?: number;
  // Short prefix (e.g. "IS" for "Island") used to number the projects
  // (boards) created in this workspace.
  abbreviation?: string | null;
  createdAt: string;
  updatedAt: string;
}

export enum WorkspaceVisibility {
  OPEN = 'open',       // Discoverable by all team members, anyone can join
  CLOSED = 'closed',   // Limited visibility, join by invitation or request
  HIDDEN = 'hidden',   // Maximum privacy, only visible to members
  // Legacy support
  PRIVATE = 'hidden',  // Alias for HIDDEN
  TEAM = 'closed',     // Alias for CLOSED
  PUBLIC = 'open',     // Alias for OPEN
}

export enum WorkspaceMemberRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

export interface WorkspaceMember {
  id: string;
  userId: string;
  user?: User;
  workspaceId: string;
  role: WorkspaceMemberRole;
  joinedAt: string;
}

export interface CreateWorkspaceDto {
  name: string;
  description?: string;
  color?: string;
  visibility: WorkspaceVisibility;
}

export interface UpdateWorkspaceDto {
  name?: string;
  description?: string;
  color?: string;
  visibility?: WorkspaceVisibility;
}

export interface AddWorkspaceMemberDto {
  email: string;
  role: WorkspaceMemberRole;
}

export interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  action: ActivityAction;
  entityType: 'task' | 'sprint' | 'board' | 'comment' | 'epic' | 'workspace';
  entityId: string;
  entityName: string;
  details?: string;
  createdAt: string;
}

export enum ActivityAction {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
  COMMENTED = 'commented',
  ASSIGNED = 'assigned',
  COMPLETED = 'completed',
  STARTED = 'started',
}

export interface Column {
  id: string;
  boardId: string;
  name: string;
  type: 'status' | 'priority' | 'assignee' | 'date' | 'text';
  order: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}
